import classNames from 'classnames';
import { graphql, Link } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import { Button } from 'hem-ui';
import get from 'lodash/get';
import isString from 'lodash/isString';
import React from 'react';

import { getLocalizedLink } from '@/utils/getLocalizedLink';

import { ImageCaption } from '../ImageCaption';
import {
  maskAccessory,
  maskAlle,
  maskAlphabeta,
  maskKendo,
  maskLast,
  maskMax,
  maskPalo,
  maskUdon,
} from './Poster.module.scss';

export const Poster: React.FC<
  Queries.PosterFragment & { isLandScape: boolean }
> = ({
  title,
  titleColor,
  buttonText,
  content,
  link,
  image,
  imageMask,
  layout = 'center',
  columnSpan,
  isLandScape,
}): JSX.Element => {
  if (!image) {
    console.warn('[Poster] Poster not rendered', {
      title,
      titleColor,
      content,
      buttonText,
      link,
      image,
      imageMask,
      layout,
      columnSpan,
    });
    return null;
  }

  // @ts-expect-error If ContentfulLink, it might have entry.slug
  const linkUrl = link?.slug || link?.entry?.slug;
  const hasLink = isString(linkUrl);
  const hasTitle = isString(title);
  const hasContent = isString(content?.childMarkdownRemark?.excerpt);
  const hasButton = hasLink && isString(buttonText);
  const hasCaption =
    get(image, 'metadata.tags', []).some(
      (tag) =>
        tag.contentful_id === 'hasCaption' ||
        tag.contentful_id === 'needsCredit',
    ) && isString(image.description);

  const renderContent = () => {
    return (
      <div
        className={classNames('relative', {
          'text-center': layout === 'center',
          'p-1': layout === 'above' || layout === 'below',
          'mb-4': layout === 'above',
          '!absolute bottom-4 left-4 md:bottom-8 md:left-8':
            layout === 'bottom-left',
        })}
      >
        {hasTitle && (
          <p
            className={classNames('font-bold group-hover:underline', {
              'text-white':
                titleColor === 'white' &&
                layout !== 'above' &&
                layout !== 'below',
              'text-blue': titleColor === 'blue',
              'text-black':
                titleColor === 'black' ||
                (titleColor === 'white' &&
                  (layout === 'above' || layout === 'below')),
              'text-hem-x': titleColor === 'hem x',
              'mb-2 text-xl':
                layout === 'bottom-left' ||
                layout === 'bottom-right' ||
                layout === 'top-left' ||
                layout === 'top-right',
              'mb-2 text-xl md:text-2xl': layout === 'center',
              'mb-1 text-base': layout === 'above' || layout === 'below',
              'mt-2': !hasCaption && layout === 'below',
            })}
          >
            {title}
          </p>
        )}

        {hasContent && (
          <p
            className={classNames('max-w-prose text-base', {
              'text-white':
                titleColor === 'white' &&
                layout !== 'above' &&
                layout !== 'below',
              'text-blue': titleColor === 'blue',
              'text-black':
                titleColor === 'black' ||
                (titleColor === 'white' &&
                  (layout === 'above' || layout === 'below')),
              'text-hem-x': titleColor === 'hem x',
              'mx-4 md:mx-8': layout === 'center',
            })}
          >
            {content.childMarkdownRemark.excerpt}
          </p>
        )}

        {hasButton && layout !== 'above' && layout !== 'below' && (
          <Button
            to={undefined}
            size="small"
            className="mt-4"
            color={titleColor === 'hem x' ? 'white' : 'blue'}
          >
            {buttonText}
          </Button>
        )}

        {hasButton && (layout === 'above' || layout === 'below') && (
          <Link
            to={undefined}
            className={classNames(
              'mt-2 inline-block font-bold group-hover:underline',
              {
                'text-blue': titleColor !== 'hem x',
                'text-hem-x': titleColor === 'hem x',
              },
            )}
          >
            {buttonText}
          </Link>
        )}
      </div>
    );
  };

  const Tag = hasLink ? Link : 'div';

  return (
    <div className="group hover:no-underline">
      {layout === 'above' && renderContent()}

      <Tag
        to={hasLink ? getLocalizedLink(linkUrl) : undefined}
        className={classNames(
          'relative grid h-full w-full overflow-hidden rounded-sm p-4 md:p-8',
          {
            'aspect-[3/2]':
              isLandScape &&
              !imageMask &&
              layout !== 'above' &&
              layout !== 'below',
            'aspect-[4/5]':
              !isLandScape &&
              !imageMask &&
              layout !== 'above' &&
              layout !== 'below',
            'aspect-square':
              layout === 'above' ||
              layout === 'below' ||
              imageMask === 'accessory',
            [maskAccessory]: imageMask === 'accessory',
            [maskAlle]: imageMask === 'alle',
            'aspect-[262/155]': imageMask === 'alle',
            [maskAlphabeta]: imageMask === 'alphabeta',
            'aspect-[200/253]': imageMask === 'alphabeta',
            [maskKendo]: imageMask === 'kendo',
            'aspect-[263/149]': imageMask === 'kendo',
            [maskLast]: imageMask === 'last',
            'aspect-[216/173]': imageMask === 'last',
            [maskMax]: imageMask === 'max',
            'aspect-[237/137]': imageMask === 'max',
            [maskPalo]: imageMask === 'palo',
            'aspect-[259/155]': imageMask === 'palo',
            [maskUdon]: imageMask === 'udon',
            'aspect-[241/179]': imageMask === 'udon',
            'place-items-center': layout === 'center',
            'place-items-start': layout === 'top-left',
            // prettier-ignore
            'place-items-end content-between text-right': layout === 'top-right',
            'place-items-end text-right': layout === 'bottom-right',
          },
        )}
      >
        <GatsbyImage
          image={image.gatsbyImageData}
          alt={image.description}
          className={classNames(
            '!absolute left-0 right-0 top-2/4 w-full -translate-y-2/4 transform',
            {
              'h-auto': !imageMask,
              'h-full': !!imageMask,
            },
          )}
        />

        {(layout === 'bottom-left' ||
          layout === 'bottom-right' ||
          layout === 'center' ||
          layout === 'top-left' ||
          layout === 'top-right') &&
          renderContent()}
      </Tag>

      {hasCaption && <ImageCaption caption={image.description} />}

      {layout === 'below' && renderContent()}
    </div>
  );
};

export const query = graphql`
  fragment Poster on ContentfulComponentPoster {
    __typename
    contentful_id
    title
    titleColor
    image {
      gatsbyImageData(
        formats: [AUTO, AVIF, WEBP]
        quality: 80
        width: 920
        placeholder: BLURRED
        resizingBehavior: FILL
      )
      description
      metadata {
        tags {
          contentful_id
        }
      }
    }
    imageMask
    buttonText
    link {
      ... on ContentfulLink {
        slug
        entry {
          ... on ContentfulPage {
            slug
          }
          ... on ContentfulPageArticle {
            slug
          }
          ... on ContentfulPageCategory {
            slug
          }
          ... on ContentfulPageDepartment {
            slug
          }
          ... on ContentfulPageDesigner {
            slug
          }
          ... on ContentfulPageFaq {
            slug
          }
          ... on ContentfulPageSubcategory {
            slug
          }
          ... on ContentfulProductVariant {
            slug
          }
        }
      }
      ... on ContentfulPage {
        slug
      }
      ... on ContentfulPageArticle {
        slug
      }
      ... on ContentfulPageCategory {
        slug
      }
      ... on ContentfulPageDepartment {
        slug
      }
      ... on ContentfulPageDesigner {
        slug
      }
      ... on ContentfulPageSubcategory {
        slug
      }
      ... on ContentfulProductVariant {
        slug
      }
    }
    content {
      childMarkdownRemark {
        excerpt(pruneLength: 210)
      }
    }
    layout
    columnSpan
  }
`;
